import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkRect, SkCircle, SkEllipse, SkLine } from "../../prim";
import { skTree, skSun } from "../../actors3";
import { skStar, skMoon } from "../../actors3/nature";
import { skChild } from "../../actors3/characters";
import type { SkinSceneProps } from "./SkinParkScene";

// ─── helpers ────────────────────────────────────────────────────────────────

/** Clamp a value to [0, 1]. */
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Linear interpolation. */
function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * clamp01(k);
}

/** Smooth step (ease-in-out) interpolation used for colour cross-fades. */
function smoothstep(a: number, b: number, k: number): number {
  const t = clamp01(k);
  return a + (b - a) * (t * t * (3 - 2 * t));
}

/** Interpolate between two hex colours by splitting into r/g/b channels. */
function lerpHex(hex1: string, hex2: string, k: number): string {
  const parse = (h: string) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const c1 = parse(hex1);
  const c2 = parse(hex2);
  const r = Math.round(lerp(c1[0], c2[0], k));
  const g = Math.round(lerp(c1[1], c2[1], k));
  const b = Math.round(lerp(c1[2], c2[2], k));
  return `rgb(${r},${g},${b})`;
}

// ════════════════════════════════════════════════════════════════════════════
// Scene 1 — SkinGravityScene
// A tree with a red apple that falls with acceleration (ease-in / quadratic),
// bounces once, while a child below looks up. Speed lines appear near ground.
// Loop: ~4 s.
// ════════════════════════════════════════════════════════════════════════════

export const SkinGravityScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "What goes up must come down!",
}) => {
  const skin = useSkin();
  const ground = 340;
  const LOOP = 4;            // seconds per loop
  const phase = t % LOOP;   // 0→4 s, loops

  // ── apple animation ──────────────────────────────────────────────────────
  // Hanging position: apple starts on the tree at x=390, y=240 (just below foliage).
  // Fall phase 0→2.5 s: accelerate downward (quadratic ease-in).
  // Bounce phase 2.5→3.2 s: bounce upward ~40 px then fall back.
  // Hold phase 3.2→4 s: apple rests on the ground.

  const APPLE_START_Y = 260;   // initial y (hanging in tree)
  const APPLE_LAND_Y  = ground - 12; // resting on grass
  const APPLE_X       = 385;

  let appleY: number;
  let appleBounceScale = 1; // squish on impact

  if (phase < 2.4) {
    // Quadratic ease-in: y = start + (land - start) * (phase/2.4)^2
    const p = phase / 2.4;
    appleY = APPLE_START_Y + (APPLE_LAND_Y - APPLE_START_Y) * (p * p);
  } else if (phase < 3.2) {
    // Bounce: quick arc back up 40 px then falls again
    const bp = (phase - 2.4) / 0.8; // 0→1 over bounce window
    const arc = Math.sin(bp * Math.PI); // 0→1→0
    appleY = APPLE_LAND_Y - arc * 40;
    // Squish at the very moment of impact (bp near 0 or 1)
    if (bp < 0.15) {
      appleBounceScale = lerp(0.6, 1, bp / 0.15);
    }
  } else {
    appleY = APPLE_LAND_Y;
  }

  // ── speed lines (appear during fall) ────────────────────────────────────
  const showSpeedLines = phase >= 1.8 && phase < 2.5;
  const slAlpha = showSpeedLines ? clamp01((phase - 1.8) / 0.3) : 0;

  // ── child: looks up during fall, then turns head back down ──────────────
  // Child stands to the right of the tree looking at the apple fall.
  // We pass wave=false; the looking-up pose is implied by placement.

  return (
    <g opacity={opacity}>
      {/* sky */}
      <rect width={800} height={450} fill={skin.surface} />

      {/* ground / grass band */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill="#86efac" seed={2000} />

      {/* sun top-left */}
      {skSun(90, 80, t, 1)}

      {/* tree — centred at x=370 */}
      {skTree(370, ground, t, 1.4)}

      {/* apple — red circle that falls */}
      <g transform={`translate(${APPLE_X} ${appleY}) scale(1 ${appleBounceScale})`}>
        <SkCircle cx={0} cy={0} r={12} fill="#ef4444" seed={2010} />
        {/* tiny stem */}
        <SkLine x1={0} y1={-12} x2={2} y2={-18} stroke="#92400e" strokeWidth={2} seed={2011} />
      </g>

      {/* speed lines near ground — three short diagonal streaks */}
      {showSpeedLines && (
        <g opacity={slAlpha}>
          {[-18, 0, 18].map((dx, i) => (
            <SkLine
              key={i}
              x1={APPLE_X + dx - 8}
              y1={appleY + 16}
              x2={APPLE_X + dx + 8}
              y2={appleY + 32}
              stroke="#f87171"
              strokeWidth={2}
              seed={2020 + i}
            />
          ))}
        </g>
      )}

      {/* child standing to the right, looking up (arm raised) */}
      {skChild(520, ground, t, 1.3, true)}

      {/* caption */}
      <g transform="translate(400 40)">
        <rect x={-240} y={-22} width={480} height={40} rx={20} fill="rgba(255,255,255,0.86)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>
          {label}
        </text>
      </g>
    </g>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Scene 2 — SkinSeasonsScene
// ONE tree cycles through the four seasons over ~12 s:
//   Spring  0–3 s  — blossoms (pink circles), skSun, sky brightens
//   Summer  3–6 s  — full green canopy, skSun high
//   Autumn  6–9 s  — falling leaves (orange/red), sky amber
//   Winter  9–12 s — bare branches, snow flakes, skMoon/skStar night sky
// Sky/ground cross-fades each transition. Season label fades in.
// ════════════════════════════════════════════════════════════════════════════

// Season colour palettes (sky top, ground, label)
const SEASON_PALETTES = [
  { sky: "#bae6fd", ground: "#bbf7d0", label: "Spring" },  // 0 spring
  { sky: "#7dd3fc", ground: "#86efac", label: "Summer" },  // 1 summer
  { sky: "#fcd34d", ground: "#fde68a", label: "Autumn" },  // 2 autumn
  { sky: "#1e3a5f", ground: "#e2e8f0", label: "Winter" },  // 3 winter
];

export const SkinSeasonsScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "The four seasons go round each year!",
}) => {
  const skin = useSkin();
  const ground = 340;
  const LOOP = 12;           // 3 s per season × 4
  const phase = t % LOOP;   // 0→12 s
  const seasonF = (phase / LOOP) * 4;  // 0→4 continuously
  const seasonIdx = Math.floor(seasonF) % 4;
  const nextIdx   = (seasonIdx + 1) % 4;
  const blend     = seasonF - Math.floor(seasonF); // 0→1 within season

  // Cross-fade colours
  const skyCol    = lerpHex(SEASON_PALETTES[seasonIdx].sky,    SEASON_PALETTES[nextIdx].sky,    smoothstep(0, 1, blend));
  const groundCol = lerpHex(SEASON_PALETTES[seasonIdx].ground, SEASON_PALETTES[nextIdx].ground, smoothstep(0, 1, blend));

  // Label: current season name fades in for first 0.5 of each season, fades out last 0.2
  const labelName = SEASON_PALETTES[seasonIdx].label;
  const labelAlpha = blend < 0.5 ? clamp01(blend / 0.15) : clamp01((1 - blend) / 0.2);

  // ── spring blossoms (pink circles scattered around canopy) ───────────────
  const inSpring  = seasonIdx === 0;
  const inSummer  = seasonIdx === 1;
  const inAutumn  = seasonIdx === 2;
  const inWinter  = seasonIdx === 3;

  // Blossom positions (deterministic offsets around the tree canopy)
  const blossomData = [
    { ox: -22, oy: -68 },
    { ox: 18,  oy: -58 },
    { ox: -8,  oy: -80 },
    { ox: 26,  oy: -74 },
    { ox: -30, oy: -55 },
    { ox: 4,   oy: -50 },
    { ox: 14,  oy: -90 },
    { ox: -14, oy: -92 },
  ];

  // Leaf positions for autumn (falling leaves — offset based on blend)
  const leafData = [
    { ox: -30, phase: 0.0, seed: 2100 },
    { ox:  10, phase: 0.2, seed: 2101 },
    { ox:  40, phase: 0.5, seed: 2102 },
    { ox: -50, phase: 0.7, seed: 2103 },
    { ox:  60, phase: 0.1, seed: 2104 },
    { ox: -20, phase: 0.9, seed: 2105 },
  ];

  // Snow flake positions for winter (gentle float down, looping on blend)
  const snowData = [
    { ox: -60, baseY: -120, speed: 0.9, seed: 2200 },
    { ox:  20, baseY: -100, speed: 0.7, seed: 2201 },
    { ox: -10, baseY: -140, speed: 1.1, seed: 2202 },
    { ox:  55, baseY: -80,  speed: 0.6, seed: 2203 },
    { ox: -80, baseY: -60,  speed: 1.3, seed: 2204 },
    { ox:  80, baseY: -130, speed: 0.8, seed: 2205 },
    { ox:  -40, baseY: -50, speed: 1.0, seed: 2206 },
  ];

  const TREE_X = 400;

  return (
    <g opacity={opacity}>
      {/* sky — cross-fades with season */}
      <rect width={800} height={450} fill={skyCol} />

      {/* ground — cross-fades with season */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill={groundCol} seed={2300} />

      {/* ── celestial bodies ─────────────────────────────────────────────── */}
      {/* Spring / Summer: sun */}
      {(inSpring || inSummer) && (
        <g opacity={inSpring ? clamp01(1 - blend * 1.5) + clamp01(blend * 10 - 9) : 1}>
          {skSun(680, 70, t, 1.1)}
        </g>
      )}
      {/* Summer sun (brighter, higher) */}
      {inSummer && (
        <g opacity={Math.min(1, blend * 4)}>
          {/* extra glow ring */}
          <SkCircle cx={680} cy={70} r={36} fill="#fef08a" seed={2310} opacity={0.35} />
        </g>
      )}
      {/* Winter: moon + stars */}
      {inWinter && (
        <g opacity={Math.min(1, blend * 3)}>
          {skMoon(680, 70, t, 1)}
          {skStar(600, 50,  t, 0.7)}
          {skStar(720, 110, t, 0.6)}
          {skStar(650, 30,  t, 0.5)}
        </g>
      )}

      {/* ── tree ─────────────────────────────────────────────────────────── */}
      {/* In winter show bare branches only — use a custom bare-branch draw */}
      {inWinter ? (
        /* bare tree: trunk + a few branch lines */
        <g transform={`translate(${TREE_X} ${ground})`}>
          <SkRect x={-7} y={-55} w={14} h={55} fill="#92400e" seed={2400} />
          {/* left branch */}
          <SkLine x1={0} y1={-48} x2={-28} y2={-80} stroke="#92400e" strokeWidth={4} seed={2401} />
          <SkLine x1={-28} y1={-80} x2={-44} y2={-96} stroke="#92400e" strokeWidth={2} seed={2402} />
          {/* right branch */}
          <SkLine x1={0} y1={-44} x2={30} y2={-75} stroke="#92400e" strokeWidth={4} seed={2403} />
          <SkLine x1={30} y1={-75} x2={42} y2={-92} stroke="#92400e" strokeWidth={2} seed={2404} />
          {/* centre top branch */}
          <SkLine x1={0} y1={-52} x2={0} y2={-90} stroke="#92400e" strokeWidth={3} seed={2405} />
        </g>
      ) : (
        skTree(TREE_X, ground, t, 1.4)
      )}

      {/* ── spring blossoms ───────────────────────────────────────────────── */}
      {inSpring &&
        blossomData.map((b, i) => {
          const appear = clamp01((blend * 8) - i * 0.5);
          return appear > 0 ? (
            <g key={i} opacity={appear}>
              <SkCircle
                cx={TREE_X + b.ox}
                cy={ground + b.oy}
                r={7}
                fill="#fbcfe8"
                seed={2500 + i}
              />
            </g>
          ) : null;
        })}

      {/* ── summer extra foliage shimmer (overlaid circles) ───────────────── */}
      {inSummer &&
        [
          { ox: -26, oy: -48, r: 14 },
          { ox:  22, oy: -44, r: 12 },
          { ox:   6, oy: -60, r: 10 },
        ].map((f, i) => (
          <SkCircle
            key={i}
            cx={TREE_X + f.ox}
            cy={ground + f.oy}
            r={f.r}
            fill="#15803d"
            seed={2550 + i}
            opacity={0.6}
          />
        ))}

      {/* ── autumn falling leaves ─────────────────────────────────────────── */}
      {inAutumn &&
        leafData.map((lf, i) => {
          // Each leaf falls from canopy level (y=-70) to ground (y=0), looping
          const localPhase = (blend + lf.phase) % 1;
          const leafY = ground - 70 + localPhase * 80;
          const leafX = TREE_X + lf.ox + Math.sin(localPhase * Math.PI * 2) * 14;
          const rot   = localPhase * 360;
          const leafColors = ["#f59e0b", "#ef4444", "#f97316", "#d97706"];
          const col = leafColors[i % leafColors.length];
          return (
            <g key={i} transform={`translate(${leafX} ${leafY}) rotate(${rot})`}>
              <SkEllipse cx={0} cy={0} rx={6} ry={4} fill={col} seed={lf.seed} />
            </g>
          );
        })}

      {/* ── winter snow flakes ────────────────────────────────────────────── */}
      {inWinter &&
        snowData.map((sf, i) => {
          const localPhase = (blend * sf.speed + i * 0.13) % 1;
          const snowY = ground + sf.baseY + localPhase * (ground + 70 - (ground + sf.baseY));
          const snowX = TREE_X + sf.ox + Math.sin(localPhase * Math.PI * 3) * 10;
          return (
            <g key={i} transform={`translate(${snowX} ${snowY})`}>
              <SkCircle cx={0} cy={0} r={4} fill="#e0f2fe" seed={sf.seed} />
            </g>
          );
        })}

      {/* ── season label (fades in/out) ───────────────────────────────────── */}
      <g transform="translate(400 150)" opacity={labelAlpha}>
        <rect x={-60} y={-20} width={120} height={36} rx={18} fill="rgba(255,255,255,0.82)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={5} textAnchor="middle" fill={skin.textInk} fontSize={18} fontWeight={800} fontFamily={skin.fontFamily}>
          {labelName}
        </text>
      </g>

      {/* ── caption ──────────────────────────────────────────────────────── */}
      <g transform="translate(400 40)">
        <rect x={-255} y={-22} width={510} height={40} rx={20} fill="rgba(255,255,255,0.86)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={15} fontWeight={700} fontFamily={skin.fontFamily}>
          {label}
        </text>
      </g>
    </g>
  );
};
